import axios from "axios"

// API Configuration - Updated for your production URL
const API_BASE_URL =
  process.env.NODE_ENV === "production" ? "https://leave-management-dhxj.onrender.com/api" : "http://localhost:5000/api"

console.log("🔗 API Base URL:", API_BASE_URL)

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Increased timeout for Render
  headers: {
    "Content-Type": "application/json",
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    console.log(`📡 API Call: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error("❌ Request Error:", error)
    return Promise.reject(error)
  },
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log("✅ API Success:", response.config.url)
    return response
  },
  (error) => {
    console.error("❌ API Error:", error)

    if (error.response?.status === 401) {
      console.log("🔐 Authentication failed - clearing token")
      localStorage.removeItem("token")
      localStorage.removeItem("user")
    }

    return Promise.reject(error)
  },
)

// Auth service object
export const authService = {
  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem("token")
    const user = localStorage.getItem("user")
    return !!(token && user)
  },

  // Get stored user data
  getUser() {
    try {
      const userStr = localStorage.getItem("user")
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {
      console.error("Error parsing stored user data:", error)
      return null
    }
  },

  // Get stored token
  getToken() {
    return localStorage.getItem("token")
  },

  // Login user
  async login(credentials) {
    const { email, password } = credentials
    const response = await api.post("/auth/login", { email, password })

    // Store token and user data if they exist in response
    if (response.data.token) {
      localStorage.setItem("token", response.data.token)
    }
    if (response.data.user) {
      localStorage.setItem("user", JSON.stringify(response.data.user))
    }

    return response.data
  },

  // Register user
  async register(userData) {
    const response = await api.post("/auth/register", userData)

    // Store token and user data if they exist in response
    if (response.data.token) {
      localStorage.setItem("token", response.data.token)
    }
    if (response.data.user) {
      localStorage.setItem("user", JSON.stringify(response.data.user))
    }

    return response.data
  },

  // Get current user from API
  async getCurrentUser() {
    const response = await api.get("/auth/me")
    return response.data
  },

  // Request password reset
  async requestPasswordReset(email) {
    const response = await api.post("/auth/forgot-password", { email })
    return response.data
  },

  // Reset password with token
  async resetPassword(token, newPassword) {
    const response = await api.post("/auth/reset-password", { 
      token, 
      newPassword 
    })
    return response.data
  },

  // Logout user
  logout() {
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    console.log("🔐 User logged out - tokens cleared")
  },
}

// User Service
export const userService = {
  getAllUsers: async () => {
    const response = await api.get("/users")
    return response.data
  },

  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },

  updateUser: async (id, userData) => {
    const response = await api.patch(`/users/${id}`, userData)
    return response.data
  },

  updatePassword: async (passwordData) => {
    const response = await api.patch("/users/update-password", passwordData)
    return response.data
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`)
    return response.data
  },

  getManagers: async () => {
    const response = await api.get("/users/managers")
    return response.data
  },

  getDepartments: async () => {
    const response = await api.get("/users/departments")
    return response.data
  },

  getBirthdays: async () => {
    const response = await api.get("/users/birthdays")
    return response.data
  },

  // Get current user info (used for refreshing user data)
  getCurrentUser: async (queryParams) => {
    const params = new URLSearchParams()
    if (queryParams && typeof queryParams === "object") {
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] !== undefined && queryParams[key] !== null) {
          params.append(key, queryParams[key])
        }
      })
    }

    const queryString = params.toString()
    const url = queryString ? `/auth/me?${queryString}` : "/auth/me"

    const response = await api.get(url)
    return response.data
  },
}

// Leave Service
export const leaveService = {
  getLeaveTypes: async () => {
    const response = await api.get("/leave/types")
    return response.data
  },

  createLeaveRequest: async (leaveData) => {
    const response = await api.post("/leave/request", leaveData)
    return response.data
  },

  createLeaveType: async (leaveTypeData) => {
    const response = await api.post("/leave/types", leaveTypeData)
    return response.data
  },

  getUserLeaveRequests: async (userId, queryParams = {}) => {
    let url = "/leave/my-requests"

    if (userId) {
      url = `/leave/user/${userId}`
    }

    const params = new URLSearchParams()
    if (queryParams && typeof queryParams === "object") {
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] !== undefined && queryParams[key] !== null) {
          params.append(key, queryParams[key])
        }
      })
    }

    const queryString = params.toString()
    if (queryString) {
      url += `?${queryString}`
    }

    const response = await api.get(url)
    return response.data
  },

  getAllLeaveRequests: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== null) {
        params.append(key, filters[key])
      }
    })

    const queryString = params.toString()
    const url = queryString ? `/leave?${queryString}` : "/leave"

    const response = await api.get(url)
    return response.data
  },

  updateLeaveRequestStatus: async (id, statusData) => {
    const response = await api.patch(`/leave/${id}/status`, statusData)
    return response.data
  },

  adjustLeaveBalance: async (userId, adjustmentData) => {
    const response = await api.post(`/leave/adjust-balance/${userId}`, adjustmentData)
    return response.data
  },

  createLeaveRequestForUser: async (leaveData) => {
    const response = await api.post("/leave/admin-request", leaveData)
    return response.data
  },

  // Get employee leave requests (for managers/admins)
  getEmployeeLeaveRequests: async (userId) => {
    const response = await api.get(`/leave/user/${userId}`)
    return response.data
  },
}

// Holiday Service
export const holidayService = {
  getAllHolidays: async (year) => {
    const url = year ? `/holidays?year=${year}` : "/holidays"
    const response = await api.get(url)
    return response.data
  },

  getHolidaysInRange: async (startDate, endDate) => {
    const response = await api.get(`/holidays/range?startDate=${startDate}&endDate=${endDate}`)
    return response.data
  },

  createHoliday: async (holidayData) => {
    const response = await api.post("/holidays", holidayData)
    return response.data
  },

  updateHoliday: async (id, holidayData) => {
    const response = await api.patch(`/holidays/${id}`, holidayData)
    return response.data
  },

  deleteHoliday: async (id) => {
    const response = await api.delete(`/holidays/${id}`)
    return response.data
  },
}

// Health Service
export const healthService = {
  check: async () => {
    const response = await api.get("/health")
    return response.data
  },
}

// Export the axios instance for other API calls
export default api